import { MusicStructureAnnotationBlock } from '../models/MusicStructureAnnotationBlock.js';
import { ChordAnnotationBlock } from '../models/ChordAnnotationBlock.js';
import { InstrumentAnnotationBlock } from '../models/InstrumentAnnotationBlock.js';

/**
 * Service for handling conversions between UI and domain data models
 */
export class ConversionService {
  constructor(annotationDataService) {
    this.dataService = annotationDataService;
  }

  /**
   * Convert UI annotation format to domain class format
   */
  uiAnnotationToDomainAnnotation(uiAnnotation) {
    // Common block position structure for all annotation types
    const blockPosition = {
      start_time: {
        bar: uiAnnotation.startMeasure,
        beat: uiAnnotation.startBeat,
        subdivision: uiAnnotation.startSubdivision
      },
      end_time: {
        bar: uiAnnotation.endMeasure,
        beat: uiAnnotation.endBeat,
        subdivision: uiAnnotation.endSubdivision
      }
    };
    
    // Handle different track types
    const trackId = uiAnnotation.trackId;
    
    if (trackId.includes('music-structure')) {
      const result = new MusicStructureAnnotationBlock({
        block_position: blockPosition,
        section_name: uiAnnotation.formData?.section_name || '',
        rhythmic_style: uiAnnotation.formData?.rhythmic_style || '',
        harmonic_progression_verbal_description: uiAnnotation.formData?.harmonic_progression_verbal_description || '',
        section_description: uiAnnotation.formData?.section_description || '',
        completed: uiAnnotation.formData?.completed || false
      });
      
      return result;
    } else if (trackId === 'chord-track') {
      return new ChordAnnotationBlock({
        block_position: blockPosition,
        chord_degree: {
          // Fields from ChordDegree section
          sharp_flat: uiAnnotation.formData?.sharp_flat || '',
          note: uiAnnotation.formData?.note || ''
        },
        chord_type: uiAnnotation.formData?.chord_type || '',
        bass: {
          // Fields from Base section
          sharp_flat: uiAnnotation.formData?.bass_sharp_flat || '',
          note: uiAnnotation.formData?.bass_note || ''
        },
        completed: uiAnnotation.formData?.completed || false,
      });
    } else {
      // Default to InstrumentAnnotationBlock for all other tracks
      const formData = uiAnnotation.formData || {};
      const fieldMappings = {
        'Instrument Function': 'instrument_function',
        'Texture': 'texture',
        'Voicing': 'voicing',
        'Rhythm Density': 'rhythm_density',
        'Rhythm Description': 'rhythm_description',
        'Secondary Melody Categorization': 'secondary_melody_categorization',
        'String Articulation Techniques': 'string_articulation_techniques',
        'Articulation Techniques': 'articulation_techniques',
        'Effects Of Articulation': 'effects_of_articulation',
        'Melody Description': 'melody_description',
        'Technique': 'technique',
        'Dynamics': 'dynamics',
        'title': 'title',
        'completed': 'completed',
      };
      const domainValues = { block_position: blockPosition };

      Object.entries(fieldMappings).forEach(([formKey, domainKey]) => {
        if (Object.prototype.hasOwnProperty.call(formData, formKey)) {
          domainValues[domainKey] = formData[formKey];
        }
      });
      Object.entries(formData)
        .filter(([key]) => !Object.prototype.hasOwnProperty.call(fieldMappings, key) && key !== 'Title')
        .forEach(([key, value]) => {
          domainValues[key.replace(/\s+/g, '_').toLowerCase()] = value;
        });

      return new InstrumentAnnotationBlock(domainValues);
    }
  }

  /**
   * Convert domain annotation to UI format
   */
  domainAnnotationToUiAnnotation(domainAnnotation, trackId, id) {
    if (!id) {
      throw new Error('domainAnnotationToUiAnnotation requires an explicit ID');
    }

    // Extract form data first to ensure it's complete
    const formData = this.extractFormDataFromDomainAnnotation(domainAnnotation, trackId);
    
    // Log the form data for debugging
    // console.log(`Converting domain annotation to UI format for ${trackId}`, {
    //   domainAnnotation,
    //   formData
    // });
    
    return {
      id,
      trackId,
      startMeasure: domainAnnotation.block_position.start_time.bar,
      startBeat: domainAnnotation.block_position.start_time.beat,
      startSubdivision: domainAnnotation.block_position.start_time.subdivision,
      endMeasure: domainAnnotation.block_position.end_time.bar,
      endBeat: domainAnnotation.block_position.end_time.beat,
      endSubdivision: domainAnnotation.block_position.end_time.subdivision,
      formData: formData // Use the extracted form data
    };
  }

  /**
   * Extract form data based on annotation type
   */
  extractFormDataFromDomainAnnotation(domainAnnotation, trackId) {
    // Add detailed logging to track the extraction process
    // console.log(`DEBUG - extractFormDataFromDomainAnnotation called for trackId: ${trackId}`);
    // console.log(`DEBUG - Domain annotation raw data:`, domainAnnotation);
    
    if (trackId.includes('music-structure')) {
      // Log each source field from the domain object for music structure
    //   console.log(`DEBUG - Music Structure source fields:`, {
    //     section_name: domainAnnotation.section_name,
    //     rhythmic_style: domainAnnotation.rhythmic_style,
    //     harmonic_progression_verbal_description: domainAnnotation.harmonic_progression_verbal_description,
    //     section_description: domainAnnotation.section_description
    //   });
      // console.log('OSDEBUG: domainAnnotation', domainAnnotation);
      
      const formData = {
        // Field names must exactly match the name property in MusicStructureAnnotationFormAttributes
        "section_name": domainAnnotation.section_name || '',
        "rhythmic_style": domainAnnotation.rhythmic_style || '',
        "harmonic_progression_verbal_description": domainAnnotation.harmonic_progression_verbal_description || '',
        "section_description": domainAnnotation.section_description || '',
        "completed": domainAnnotation.completed || false
      };
      
      // Log the final form data being sent to the form
      // console.log(`DEBUG - Extracted Music Structure form data:`, formData);
      return formData;
    } else if (trackId === 'chord-track') {
      // Extract data from domain annotation
      // Note both MovableDoChordAttributes and FixedDoChordAttributes structures have:
      // - ChordDegree (fields for sharp_flat and note)
      // - ChordType (field for chord_type)
      // - Base (fields for sharp_flat and note)
      
    //   console.log(`DEBUG - Chord Track source fields:`, {
    //     chord_degree: domainAnnotation.chord_degree,
    //     chord_type: domainAnnotation.chord_type,
    //     bass: domainAnnotation.bass
    //   });
      
      // We need to construct an object that matches the form field structure with section prefixes
      const formData = {};
      
      // Fields from ChordDegree section
      // These fields go into the ChordDegree section
      formData["sharp_flat"] = domainAnnotation.chord_degree?.sharp_flat || '';
      formData["note"] = domainAnnotation.chord_degree?.note || '';
      
      // Field from ChordType section
      // This field goes into the ChordType section  
      formData["chord_type"] = domainAnnotation.chord_type || '';
      
      // Fields from Base section
      // These fields go into the Base section
      formData["bass_sharp_flat"] = domainAnnotation.bass?.sharp_flat || '';
      formData["bass_note"] = domainAnnotation.bass?.note || '';
      formData['completed'] = domainAnnotation.completed || false;
      
      // Log the final form data being sent to the form
      // console.log(`DEBUG - Extracted Chord Track form data:`, formData);
      return formData;
    } else {
      // Handle generic instrument tracks
      // Extract all properties except block_position
      const formData = {};
      if (Object.prototype.hasOwnProperty.call(domainAnnotation, 'title')) {
        formData.title = domainAnnotation.title;
      }
      if (Object.prototype.hasOwnProperty.call(domainAnnotation, 'completed')) {
        formData.completed = domainAnnotation.completed;
      }
      
      // Direct mapping from snake_case domain properties to Title Case form field names
      // This ensures data from the backend is correctly mapped to form field names
      const fieldMappings = {
        'instrument_function': 'Instrument Function',
        'texture': 'Texture',
        'voicing': 'Voicing',
        'rhythm_density': 'Rhythm Density',
        'rhythm_description': 'Rhythm Description',
        'secondary_melody_categorization': 'Secondary Melody Categorization',
        'string_articulation_techniques': 'String Articulation Techniques',
        'articulation_techniques': 'Articulation Techniques',
        'effects_of_articulation': 'Effects Of Articulation',
        'melody_description': 'Melody Description',
        'technique': 'Technique',
        'dynamics': 'Dynamics',
      };
      
      // Add standard properties with the correct field names expected by the form
      Object.entries(fieldMappings).forEach(([domainKey, formKey]) => {
        if (domainAnnotation[domainKey] !== undefined) {
          formData[formKey] = domainAnnotation[domainKey];
        }
      });
      
      // Also add any other properties that might be in the domain annotation
      Object.entries(domainAnnotation).forEach(([key, value]) => {
        if (key !== 'block_position' && key !== 'completed' && !Object.keys(fieldMappings).includes(key) && key !== 'title') {
          // Convert snake_case to Title Case for display
          const displayKey = key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          formData[displayKey] = value;
        }
      });
      
      // Add a console log to debug what data is being extracted
      // console.log(`Extracted form data for ${trackId}:`, formData);
      
      return formData;
    }
  }
}
